// src/pages/Officer/OfficerDashboard.jsx
import React, { useState, useEffect, useContext, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Stack,
  Chip,
  IconButton,
  Alert,
  Snackbar,
  alpha,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Refresh,
  WifiOff,
  Wifi,
  Speed,
} from '@mui/icons-material';
import AuthContext from '../../contexts/AuthContext';
import { dashboardAPI, alertAPI, monitoringAPI } from '../../services/api';
import useAlertWebSocket from '../../hooks/useAlertWebSocket';
import { useNotifications } from '../../hooks/useNotifications';

// Sub-components
import MetricsPanel from './components/MetricsPanel';
import AlertStream from './components/AlertStream';
import ConditionMonitor from './components/ConditionMonitor';
import StrategyStatusList from './components/StrategyStatusList';

/**
 * OfficerDashboard - Professional trading dashboard with real-time monitoring
 */
const OfficerDashboard = () => {
  const { user } = useContext(AuthContext);
  
  // State management
  const [dashboardState, setDashboardState] = useState({
    loading: true,
    refreshing: false,
    error: null,
    lastUpdate: null,
  });
  
  const [metrics, setMetrics] = useState({
    activeStrategies: 0,
    totalSignals: 0,
    successRate: 0,
    recentAlerts: [],
  });
  
  const [liveData, setLiveData] = useState({
    alerts: [],
    conditionUpdates: [],
    activeStrategies: {},
  });

  // Historical alerts from database
  const [historicalAlerts, setHistoricalAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  const { showNotification, playNotificationSound } = useNotifications(true);

  // Ref for debouncing updates
  const updateTimeoutRef = useRef(null);

  /**
   * Fetch historical alerts from database
   */
  const fetchHistoricalAlerts = useCallback(async () => {
    try {
      setAlertsLoading(true);
      const result = await alertAPI.getHistoricalAlerts({
        limit: 100,
      });

      if (result.success) {
        setHistoricalAlerts(result.data.alerts || []);
        console.log('[Dashboard] Loaded', result.data.alerts?.length, 'historical alerts');
      } else {
        console.error('[Dashboard] Failed to fetch historical alerts:', result.message);
      }
    } catch (err) {
      console.error('[Dashboard] Error fetching historical alerts:', err);
    } finally {
      setAlertsLoading(false);
    }
  }, []);

  /**
   * Fetch monitoring history (24 hours) - Better merging
   */
  const fetchMonitoringHistory = useCallback(async () => {
    try {
      console.log('[Dashboard] Fetching 24-hour monitoring history...');
      
      // Fetch condition history
      const conditionResult = await monitoringAPI.getConditionHistory({ hours: 24 });
      
      // Fetch strategy activity
      const strategyResult = await monitoringAPI.getStrategyActivity(24);
      
      if (conditionResult.success && strategyResult.success) {
        // Build condition updates from history
        const historicalConditionUpdates = conditionResult.data.condition_updates.map(update => ({
          id: `hist-${update.id}`,
          timestamp: update.detected_at,
          strategy_id: update.strategy_id,
          condition_results: {
            [update.condition_name]: {
              condition_met: update.condition_met,
              condition_type: update.condition_type,
              signal_data: update.signal_data,
            }
          }
        }));
        
        // Build strategy states from history - Use reduce for stable object
        const historicalStrategies = strategyResult.data.strategies.reduce((acc, strategy) => {
          acc[strategy.strategy_id] = {
            strategy_id: strategy.strategy_id,
            symbol: strategy.symbol,
            timeframe: strategy.timeframe,
            conditions: strategy.conditions,
            condition_states: strategy.condition_states || {},
            signal_count_24h: strategy.signal_count_24h || 0,
            last_signal: strategy.last_signal,
            lastUpdate: strategy.last_update,
          };
          return acc;
        }, {});
        
        setLiveData(prev => {
          // Merge strategies without replacing existing live data
          const mergedStrategies = { ...historicalStrategies };
          
          // Overlay existing live data on top of historical
          Object.keys(prev.activeStrategies).forEach(strategyId => {
            if (mergedStrategies[strategyId]) {
              mergedStrategies[strategyId] = {
                ...mergedStrategies[strategyId],
                ...prev.activeStrategies[strategyId],
                // Ensure we keep the better symbol/timeframe
                symbol: prev.activeStrategies[strategyId].symbol || mergedStrategies[strategyId].symbol,
                timeframe: prev.activeStrategies[strategyId].timeframe || mergedStrategies[strategyId].timeframe,
              };
            }
          });
          
          return {
            ...prev,
            conditionUpdates: [
              ...historicalConditionUpdates,
              ...prev.conditionUpdates
            ].slice(0, 200), // Keep last 200
            activeStrategies: mergedStrategies,
          };
        });
        
        console.log('[Dashboard] Loaded', historicalConditionUpdates.length, 'condition updates');
        console.log('[Dashboard] Loaded', Object.keys(historicalStrategies).length, 'strategies');
      }
    } catch (err) {
      console.error('[Dashboard] Error fetching monitoring history:', err);
    }
  }, []);

  /**
   * WebSocket handler for condition updates - Better merging with debouncing
   */
  const handleConditionUpdate = useCallback((data) => {
    console.log('[WebSocket] Condition Update:', data);
    
    // Clear existing timeout
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    
    // Debounce updates by 100ms
    updateTimeoutRef.current = setTimeout(() => {
      setLiveData(prev => {
        const strategyId = data.strategy_id;
        
        // Check if this update already exists (avoid duplicates)
        const exists = prev.conditionUpdates.some(
          update => update.strategy_id === strategyId && 
                    Math.abs(new Date(update.timestamp) - new Date(data.timestamp)) < 1000
        );
        
        if (exists) {
          console.log('[Dashboard] Duplicate update detected, skipping');
          return prev;
        }
        
        // Get existing strategy data
        const existingStrategy = prev.activeStrategies[strategyId];
        
        // Build new strategy data by MERGING (not replacing)
        const updatedStrategy = {
          // Start with existing data
          ...existingStrategy,
          // Add/update with new data
          strategy_id: strategyId,
          symbol: data.symbol || existingStrategy?.symbol || 'Loading...',
          timeframe: data.timeframe || existingStrategy?.timeframe || 'Loading...',
          conditions: data.conditions || existingStrategy?.conditions || [],
          condition_states: {
            // Keep existing condition states
            ...(existingStrategy?.condition_states || {}),
            // Merge with new condition results
            ...Object.entries(data.condition_results || {}).reduce((acc, [name, result]) => {
              acc[name] = {
                state: result,
                updated_at: data.timestamp || new Date().toISOString(),
              };
              return acc;
            }, {}),
          },
          signal_count_24h: data.signal_count_24h || existingStrategy?.signal_count_24h || 0,
          lastUpdate: data.timestamp || new Date().toISOString(),
        };
        
        console.log('[Dashboard] Updated strategy:', updatedStrategy);
        
        return {
          ...prev,
          activeStrategies: {
            ...prev.activeStrategies,
            [strategyId]: updatedStrategy,
          },
          conditionUpdates: [
            { 
              id: `${strategyId}-${Date.now()}`, 
              timestamp: data.timestamp || new Date().toISOString(), 
              ...data 
            },
            ...prev.conditionUpdates
          ].slice(0, 200), // Keep last 200
        };
      });
    }, 100);
  }, []);

  /**
   * WebSocket handler for alert triggers
   */
  const handleAlertTriggered = useCallback((data) => {
    console.log('[WebSocket] Alert Triggered:', data);
    
    const newAlert = {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      ...data
    };
    
    setLiveData(prev => ({
      ...prev,
      alerts: [newAlert, ...prev.alerts.slice(0, 99)],
    }));

    // Also add to historical alerts for persistence
    setHistoricalAlerts(prev => [newAlert, ...prev.slice(0, 99)]);

    // Notifications
    playNotificationSound('alert', 'high');
    showNotification({
      title: `Alert: ${data.strategy_id}`,
      body: `${data.alerts?.length || 0} conditions met`,
      tag: data.strategy_id,
    });

    // Refresh metrics on new alert
    fetchDashboardData(false);
  }, [playNotificationSound, showNotification]);

  /**
   * WebSocket connection hook
   */
  const { isConnected, connectionQuality } = useAlertWebSocket(
    handleConditionUpdate,
    handleAlertTriggered
  );

  /**
   * Fetch dashboard metrics from API
   */
  const fetchDashboardData = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) {
        setDashboardState(prev => ({ ...prev, loading: true, error: null }));
      } else {
        setDashboardState(prev => ({ ...prev, refreshing: true }));
      }

      const result = await dashboardAPI.getStats();

      if (result.success) {
        setMetrics({
          activeStrategies: result.data.active_strategies,
          totalSignals: result.data.total_signals_today,
          successRate: result.data.success_rate,
          recentAlerts: result.data.recent_alerts || [],
        });
        
        setDashboardState(prev => ({
          ...prev,
          loading: false,
          refreshing: false,
          error: null,
          lastUpdate: new Date().toISOString(),
        }));
      } else {
        throw new Error(result.message || 'Failed to fetch dashboard data');
      }
    } catch (err) {
      console.error('[Dashboard] Error fetching data:', err);
      setDashboardState(prev => ({
        ...prev,
        loading: false,
        refreshing: false,
        error: err.message || 'An unexpected error occurred',
      }));
    }
  }, []);

  /**
   * Initial data fetch
   */
  useEffect(() => {
    const loadInitialData = async () => {
      await Promise.all([
        fetchDashboardData(true),
        fetchHistoricalAlerts(),
        fetchMonitoringHistory(),
      ]);
      setInitialLoadComplete(true);
    };
    
    loadInitialData();
  }, [fetchDashboardData, fetchHistoricalAlerts, fetchMonitoringHistory]);

  /**
   * Auto-refresh every 30 seconds
   */
  useEffect(() => {
    const interval = setInterval(() => {
      fetchDashboardData(false);
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  /**
   * Cleanup timeout on unmount
   */
  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Manual refresh handler
   */
  const handleRefresh = useCallback(() => {
    fetchDashboardData(false);
    fetchHistoricalAlerts();
    fetchMonitoringHistory();
  }, [fetchDashboardData, fetchHistoricalAlerts, fetchMonitoringHistory]);

  /**
   * Dismiss error handler
   */
  const handleDismissError = useCallback(() => {
    setDashboardState(prev => ({ ...prev, error: null }));
  }, []);

  /**
   * Combined alerts: merge live alerts with historical alerts
   * Remove duplicates and sort by timestamp
   */
  const allAlerts = useMemo(() => {
    const combined = [
      ...liveData.alerts,
      ...historicalAlerts,
      ...metrics.recentAlerts,
    ];

    // Remove duplicates based on ID or unique identifier
    const uniqueAlerts = Array.from(
      new Map(
        combined.map(alert => {
          // Create unique key
          const key = alert.id || `${alert.strategy_id || alert.strategy}-${alert.timestamp}`;
          return [key, alert];
        })
      ).values()
    );

    // Sort by timestamp descending (newest first)
    return uniqueAlerts.sort((a, b) => {
      const dateA = new Date(a.timestamp || a.detected_at || 0);
      const dateB = new Date(b.timestamp || b.detected_at || 0);
      return dateB - dateA;
    }).slice(0, 100); // Keep last 100
  }, [liveData.alerts, historicalAlerts, metrics.recentAlerts]);

  /**
   * Combined metrics for MetricsPanel
   */
  const combinedMetrics = useMemo(() => ({
    activeStrategies: metrics.activeStrategies,
    totalSignals: metrics.totalSignals,
    successRate: metrics.successRate,
    liveStrategies: Object.keys(liveData.activeStrategies).length,
    totalAlerts: allAlerts.length,
    recentUpdates: liveData.conditionUpdates.length,
  }), [metrics, liveData, allAlerts]);

  /**
   * Loading state
   */
  if (dashboardState.loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: '#0a0e14',
          gap: 2,
        }}
      >
        <CircularProgress size={48} thickness={4} sx={{ color: '#00d4ff' }} />
        <Typography variant="body1" sx={{ color: 'grey.400' }}>
          Loading dashboard...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: '#0a0e14',
        minHeight: '100vh',
        color: 'grey.100',
      }}
    >
      {/* Error Snackbar */}
      <Snackbar
        open={!!dashboardState.error}
        autoHideDuration={6000}
        onClose={handleDismissError}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleDismissError}
          severity="error"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {dashboardState.error}
        </Alert>
      </Snackbar>

      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          bgcolor: alpha('#0f1419', 0.95),
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid',
          borderColor: alpha('#00d4ff', 0.1),
        }}
      >
        <Container maxWidth={false} sx={{ maxWidth: 1920 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ py: 2 }}
          >
            {/* Left: Title */}
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  background: 'linear-gradient(135deg, #00d4ff 0%, #0066ff 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 4px 12px ${alpha('#00d4ff', 0.3)}`,
                }}
              >
                <Speed sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: '-0.02em',
                    color: 'grey.50',
                  }}
                >
                  Trading Dashboard
                </Typography>
                <Typography variant="caption" sx={{ color: 'grey.500' }}>
                  Welcome back, {user?.full_name?.split(' ')[0]}
                </Typography>
              </Box>
            </Stack>

            {/* Right: Controls */}
            <Stack direction="row" spacing={1.5} alignItems="center">
              {/* Connection Status */}
              <Tooltip
                title={
                  isConnected
                    ? `Connected • Quality: ${connectionQuality || 'Good'}`
                    : 'Disconnected'
                }
              >
                <Chip
                  icon={isConnected ? <Wifi /> : <WifiOff />}
                  label={isConnected ? 'LIVE' : 'OFFLINE'}
                  size="small"
                  sx={{
                    height: 28,
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    letterSpacing: '0.05em',
                    bgcolor: isConnected
                      ? alpha('#00ff88', 0.15)
                      : alpha('#ff4444', 0.15),
                    color: isConnected ? '#00ff88' : '#ff4444',
                    border: '1px solid',
                    borderColor: isConnected
                      ? alpha('#00ff88', 0.3)
                      : alpha('#ff4444', 0.3),
                    '& .MuiChip-icon': {
                      color: 'inherit',
                    },
                  }}
                />
              </Tooltip>

              {/* Last Update */}
              {dashboardState.lastUpdate && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'grey.600',
                    fontFamily: 'monospace',
                  }}
                >
                  {new Date(dashboardState.lastUpdate).toLocaleTimeString()}
                </Typography>
              )}

              {/* Refresh Button */}
              <Tooltip title="Refresh data">
                <IconButton
                  onClick={handleRefresh}
                  disabled={dashboardState.refreshing || alertsLoading}
                  sx={{
                    bgcolor: alpha('#00d4ff', 0.1),
                    border: '1px solid',
                    borderColor: alpha('#00d4ff', 0.2),
                    '&:hover': {
                      bgcolor: alpha('#00d4ff', 0.2),
                    },
                    '&:disabled': {
                      bgcolor: alpha('#666', 0.1),
                    },
                  }}
                >
                  <Refresh
                    sx={{
                      color: '#00d4ff',
                      animation: (dashboardState.refreshing || alertsLoading)
                        ? 'spin 1s linear infinite'
                        : 'none',
                    }}
                  />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Container>
      </Paper>

      {/* Main Content */}
      <Container maxWidth={false} sx={{ maxWidth: 1920, py: 3 }}>
        <Stack spacing={3}>
          {/* Metrics Panel */}
          <MetricsPanel metrics={combinedMetrics} />

          {/* Strategy Status - Only render when initial load is complete */}
          {initialLoadComplete && (
            <StrategyStatusList
              activeStrategies={liveData.activeStrategies}
              isConnected={isConnected}
            />
          )}

          {/* Alert Stream & Condition Monitor */}
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3}>
            <Box sx={{ flex: 1 }}>
              <AlertStream
                alerts={allAlerts}
                loading={alertsLoading}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <ConditionMonitor updates={liveData.conditionUpdates} />
            </Box>
          </Stack>
        </Stack>
      </Container>

      {/* CSS for animations */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Box>
  );
};

export default OfficerDashboard;
